import { FINDY_REFERRER_TAGS } from '@findy-code/yuffie/models';
import { Stack } from '@findy-code/yuffie/ui/stack';
import type { ReactNode } from 'react';

import {
  HamburgerMenuButtonItem,
  HamburgerMenuExternalLinkItem,
  HamburgerMenuLinkItem,
} from '../../../components/hamburger-menu/hamburger-menu-item';
import { InlineCountBadge } from '../count-badge.component';
import styles from './main-hamburger-menu-list.component.module.scss';

type Props = {
  children: ReactNode;
  isFreelanceUser: boolean;
  isTargetUser: boolean;
  profileCompletionProgress: number;
  unreadMessageCount: number;
  pathname: string;
  onLogout: () => void;
};

// MEMO:
// プロフィールと設定リンクのhrefは動的に設定したほうが良いかも
// 例えば現在のpathが/settings/accountの時に プロフィールをクリックすると
// ページは/settings/profileに遷移するが、ハンバーガーメニューが開かれたままになってしまう
// /settingsはdynamic routesなのでページコンポーネントがアンマウントされないためこのような挙動になる
export const MainHamburgerMenuList = ({
  children: CareerMotivationButton,
  isFreelanceUser,
  isTargetUser,
  profileCompletionProgress,
  unreadMessageCount,
  pathname,
  onLogout,
}: Props) => {
  return (
    <Stack>
      {CareerMotivationButton}
      <ul className={styles.menu}>
        <HamburgerMenuLinkItem
          href="/home"
          isCurrentPage={pathname === '/home'}
        >
          マイページ
        </HamburgerMenuLinkItem>
        <HamburgerMenuLinkItem
          href="/recommends"
          isCurrentPage={
            pathname === '/recommends' || pathname === '/companies'
          }
        >
          求人・企業検索
        </HamburgerMenuLinkItem>
        <HamburgerMenuLinkItem
          href="/likes"
          isCurrentPage={pathname.startsWith('/likes')}
        >
          いいね
        </HamburgerMenuLinkItem>
        <HamburgerMenuLinkItem
          href="/matches"
          isCurrentPage={pathname.startsWith('/matches')}
        >
          <span
            style={{
              display: 'inline-flex',
              gap: '4px',
              justifyContent: 'center',
            }}
          >
            メッセージ
            {unreadMessageCount > 0 && (
              <InlineCountBadge count={unreadMessageCount} />
            )}
          </span>
        </HamburgerMenuLinkItem>
        <HamburgerMenuLinkItem
          href="/pick-up"
          isCurrentPage={pathname.startsWith('/pick-up')}
        >
          メディア
        </HamburgerMenuLinkItem>
        <HamburgerMenuLinkItem
          href="/events"
          isCurrentPage={pathname.startsWith('/events')}
        >
          イベント
        </HamburgerMenuLinkItem>
        <HamburgerMenuLinkItem
          href="/settings/profile"
          isCurrentPage={/^\/settings\/(account|profile|personality|desired_condition|resume)/i.test(
            pathname
          )}
        >
          プロフィール
          {profileCompletionProgress !== 100 && (
            <span
              aria-label="プロフィールに未入力項目があります"
              className={styles.profileIncompletionMark}
            />
          )}
        </HamburgerMenuLinkItem>
        {!isFreelanceUser && isTargetUser && (
          <HamburgerMenuLinkItem
            href="/us-support"
            isCurrentPage={pathname.startsWith('/us-support')}
          >
            転職サポート
          </HamburgerMenuLinkItem>
        )}
      </ul>
      {isFreelanceUser && (
        <>
          <hr className={styles.divider} />
          <ul className={styles.menu}>
            <HamburgerMenuExternalLinkItem
              href={`https://freelance.findy-code.io/?utm_source=fc_mypage&fr=${FINDY_REFERRER_TAGS.FINDY_FREELANCE_REFERRAL}`}
              icon={
                <svg
                  fill="none"
                  height="16"
                  style={{ alignSelf: 'baseline' }}
                  viewBox="0 0 16 16"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.4001 9.59998H11.6001C11.494 9.59998 11.3923 9.64212 11.3173 9.71713C11.2422 9.79215 11.2001 9.89389 11.2001 9.99998V12.8H3.2001V4.79998H6.8001C6.90618 4.79998 7.00793 4.75783 7.08294 4.68282C7.15795 4.6078 7.2001 4.50606 7.2001 4.39998V3.59998C7.2001 3.49389 7.15795 3.39215 7.08294 3.31713C7.00793 3.24212 6.90618 3.19998 6.8001 3.19998H2.8001C2.48184 3.19998 2.17661 3.3264 1.95157 3.55145C1.72653 3.77649 1.6001 4.08172 1.6001 4.39998L1.6001 13.2C1.6001 13.5182 1.72653 13.8235 1.95157 14.0485C2.17661 14.2735 2.48184 14.4 2.8001 14.4H11.6001C11.9184 14.4 12.2236 14.2735 12.4486 14.0485C12.6737 13.8235 12.8001 13.5182 12.8001 13.2V9.99998C12.8001 9.89389 12.758 9.79215 12.6829 9.71713C12.6079 9.64212 12.5062 9.59998 12.4001 9.59998ZM13.8001 1.59998H10.6001C10.0658 1.59998 9.79885 2.24773 10.1751 2.62498L11.0683 3.51823L4.9751 9.60923C4.91916 9.66497 4.87478 9.7312 4.8445 9.80413C4.81421 9.87707 4.79862 9.95526 4.79862 10.0342C4.79862 10.1132 4.81421 10.1914 4.8445 10.2643C4.87478 10.3372 4.91916 10.4035 4.9751 10.4592L5.54185 11.025C5.59759 11.0809 5.66383 11.1253 5.73676 11.1556C5.80969 11.1859 5.88788 11.2014 5.96685 11.2014C6.04582 11.2014 6.12401 11.1859 6.19694 11.1556C6.26987 11.1253 6.33611 11.0809 6.39185 11.025L12.4821 4.93298L13.3751 5.82498C13.7501 6.19998 14.4001 5.93748 14.4001 5.39998V2.19998C14.4001 2.04085 14.3369 1.88823 14.2244 1.77571C14.1118 1.66319 13.9592 1.59998 13.8001 1.59998V1.59998Z"
                    fill="#333333"
                  />
                </svg>
              }
              rel="noopener"
              target="_blank"
            >
              フリーランス案件
            </HamburgerMenuExternalLinkItem>
          </ul>
        </>
      )}
      <hr className={styles.divider} />
      <ul className={styles.menu}>
        <HamburgerMenuLinkItem
          href="/settings/block_setting"
          isCurrentPage={/^\/settings\/(block_setting|notifications|user_success_plus)/i.test(
            pathname
          )}
        >
          設定
        </HamburgerMenuLinkItem>
        <HamburgerMenuLinkItem
          href="/others/inquiry"
          isCurrentPage={pathname === '/others/inquiry'}
        >
          お問い合わせ・退会
        </HamburgerMenuLinkItem>
        <HamburgerMenuButtonItem onClick={onLogout}>
          ログアウト
        </HamburgerMenuButtonItem>
      </ul>
    </Stack>
  );
};
